import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, X } from "lucide-react";

const CLOSE_MS = 480;
const TYPING = 'input,textarea,select,[contenteditable="true"],[contenteditable=""]';
const INTERACTIVE = 'a,button,input,select,textarea,label,summary,[role="button"],[contenteditable="true"]';
const MODAL = '[aria-modal="true"]';

// Router state on links that open a split detail: Back from the detail returns to the list.
export const SPLIT_STATE = { split: true };

// Row link props: opening from the list adds one history entry (marked, so Close can step back);
// switching records while open replaces it and keeps that mark, so Back still returns to the list.
export function useSplitLink(selectedId) {
  const { state } = useLocation();
  return selectedId
    ? { "data-split-link": "", replace: true, state: state?.split ? SPLIT_STATE : null }
    : { "data-split-link": "", replace: false, state: SPLIT_STATE };
}

// Close a URL-driven split: step back when the list is the previous entry, otherwise go to it.
export function useSplitClose(base) {
  const nav = useNavigate(), location = useLocation();
  return useCallback(() => {
    if (location.state?.split) nav(-1);
    else nav({ pathname: base, search: location.search }, { replace: true });
  }, [nav, base, location.state, location.search]);
}

const readWidth = (el) => (el ? el.getBoundingClientRect().width : 0);
const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

// iPad-style split view. Wide: the list squeezes left and the detail card slides in on the right.
// Medium: the card floats over the list. Narrow: the detail replaces the list (push).
// The last detail stays on screen until the close animation ends.
export function SplitView({
  list, detail, detailKey, open, onClose, title, closeLabel = "Close", backLabel = "Back",
  splitAt = 880, floatAt = 640, className = "",
}) {
  const nav = useNavigate(), location = useLocation();
  const root = useRef(null), listRef = useRef(null), card = useRef(null), returnTo = useRef(null), kept = useRef(null);
  const listScroll = useRef(null);
  const [width, setWidth] = useState(splitAt);
  const [phase, setPhase] = useState(open ? "open" : "closed");
  const mode = width >= splitAt ? "split" : width >= floatAt ? "float" : "stack";
  if (open) kept.current = { detail, title, detailKey };
  const shown = phase === "closed" && !open ? null : kept.current;
  const modal = open && mode === "float";
  const latest = useRef({});
  latest.current = { open, onClose, mode, state: location.state };

  useLayoutEffect(() => {
    const el = root.current;
    setWidth(readWidth(el) || splitAt);
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => setWidth(readWidth(el)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [splitAt]);

  const rowFor = (key) => {
    const links = [...(listRef.current?.querySelectorAll("a[data-split-link]") || [])];
    return links.find((a) => a.getAttribute("aria-current") === "true")
      || (key ? links.find((a) => (a.getAttribute("href") || "").endsWith(`/${encodeURIComponent(key)}`)) : null);
  };

  const wasOpen = useRef(open);
  useEffect(() => {
    const before = wasOpen.current;
    wasOpen.current = open;
    if (open) {
      setPhase("open");
      // Focus goes back to the row of the record now showing, however it was chosen.
      const row = rowFor(detailKey);
      if (row) returnTo.current = row;
      if (before) return undefined;
      if (!returnTo.current && !card.current?.contains(document.activeElement)) returnTo.current = document.activeElement;
      // Phones: the detail replaces the list, so start it at the top and remember the list position.
      if (latest.current.mode === "stack" && typeof window !== "undefined") {
        listScroll.current = window.scrollY;
        root.current?.scrollIntoView?.({ block: "start" });
      }
      const raf = requestAnimationFrame(() => card.current?.focus({ preventScroll: true }));
      return () => cancelAnimationFrame(raf);
    }
    if (!before) return undefined;
    setPhase("closing");
    const focusLost = !document.activeElement || document.activeElement === document.body || card.current?.contains(document.activeElement);
    const restore = listScroll.current;
    listScroll.current = null;
    const back = () => {
      if (restore != null) window.scrollTo(0, restore);
      if (!focusLost) return;
      const target = [returnTo.current, rowFor(kept.current?.detailKey), listRef.current?.querySelector("a[data-split-link]"), listRef.current]
        .find((el) => el?.isConnected);
      target?.focus({ preventScroll: true });
    };
    const raf = requestAnimationFrame(back);
    const t = setTimeout(() => { kept.current = null; returnTo.current = null; setPhase("closed"); }, CLOSE_MS);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, detailKey]);

  // Esc closes; j/k (and arrows on a row) move between rows, switching the record while the card is
  // open. Keys count only when they come from this view (or the page itself), never from a form.
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented) return;
      const el = root.current, target = e.target;
      if (!el || target?.closest?.(TYPING) || target?.closest?.("form") || document.querySelector(MODAL + ":not(.ios-split-card)")) return;
      const fromPage = !(target instanceof Node) || target === document.body || target === document.documentElement;
      if (!fromPage && !el.contains(target)) return;
      const { open: isOpen, onClose: close, mode: now, state } = latest.current;
      if (e.key === "Escape") {
        if (!isOpen) return;
        e.preventDefault();
        close?.();
        return;
      }
      const onRow = Boolean(target?.closest?.("a[data-split-link]")) && listRef.current?.contains(target);
      const step = e.key === "j" || (onRow && e.key === "ArrowDown") ? 1 : e.key === "k" || (onRow && e.key === "ArrowUp") ? -1 : 0;
      if (!step || !(onRow || isOpen)) return;
      const links = [...listRef.current.querySelectorAll("a[data-split-link]")];
      if (!links.length) return;
      const at = links.indexOf(target?.closest?.("a[data-split-link]"));
      const from = at >= 0 ? at : links.findIndex((a) => a.getAttribute("aria-current") === "true");
      const next = links[from < 0 ? 0 : Math.min(links.length - 1, Math.max(0, from + step))];
      e.preventDefault();
      if (isOpen && next.getAttribute("aria-current") !== "true") nav(next.getAttribute("href"), { replace: true, state });
      if (now === "split" || !isOpen) {
        next.focus();
        next.scrollIntoView?.({ block: "nearest" });
      } else requestAnimationFrame(() => card.current?.focus({ preventScroll: true }));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nav]);

  // A floating card is modal: Tab cycles inside it.
  const onCardKeyDown = (e) => {
    if (!modal || e.key !== "Tab") return;
    const items = [...card.current.querySelectorAll(FOCUSABLE)].filter((n) => n.offsetParent !== null || n === document.activeElement);
    if (!items.length) { e.preventDefault(); return; }
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === card.current)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  // The whole row opens its record; links, buttons and text selection keep their own meaning.
  const onListClick = (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (e.target.closest(INTERACTIVE)) return;
    const row = e.target.closest("[data-split-row]");
    if (!row || String(window.getSelection?.() || "")) return;
    row.querySelector("a[data-split-link]")?.click();
  };

  const listInert = modal || (open && mode === "stack");
  return (
    <div ref={root} className={`ios-split ${className}`} data-mode={mode} data-open={open ? "true" : "false"} data-phase={phase}>
      <div className="ios-split-grid">
        <div ref={listRef} className="ios-split-list" tabIndex={-1} onClick={onListClick} {...(listInert ? { inert: "" } : {})}>{list}</div>
        {modal && <div className="ios-split-scrim" aria-hidden="true" onClick={onClose} />}
        <div className="ios-split-detail">
          {shown && (
            <section
              ref={card}
              className="ios-split-card"
              data-glass={mode === "stack" ? undefined : "card"}
              tabIndex={-1}
              role={modal ? "dialog" : "region"}
              aria-modal={modal || undefined}
              aria-label={shown.title}
              onKeyDown={onCardKeyDown}
            >
              <header className="ios-split-head">
                {mode === "stack" ? (
                  <button type="button" className="ios-btn ios-btn-plain ios-split-back" onClick={onClose}>
                    <ChevronLeft size={18} aria-hidden="true" /> {backLabel}
                  </button>
                ) : (
                  <span className="ios-split-caption">{shown.title}</span>
                )}
                {mode !== "stack" && (
                  <button type="button" className="ios-btn ios-btn-gray ios-icon-btn ios-split-close" aria-label={closeLabel} onClick={onClose}>
                    <X size={16} aria-hidden="true" />
                  </button>
                )}
              </header>
              <div className="ios-split-body" key={shown.detailKey}>{shown.detail}</div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
